import type { SelectChangeEvent } from '@mui/material';
import { Box, FormControl, InputLabel, Chip, MenuItem, ListItemText, Select, Checkbox } from '@mui/material';
import { isEmptyArray } from '@vy/utils';
import { usePageParam } from '../../../../../hooks/search-params/use-page-param';

type FilterSelectProps<Filter extends string> = {
  label: string;
  options: Filter[];
  selectedValues: Filter[];
  setSelectedValues: (values: Filter[]) => void;
  getLabel: (value: Filter) => string;
  selectStyle?: React.CSSProperties;
};

export const FilterSelect = <Filter extends string>({
  label,
  options,
  selectedValues,
  setSelectedValues,
  getLabel,
  selectStyle,
}: FilterSelectProps<Filter>) => {
  const [_page, setPage] = usePageParam();

  const handleChange = (event: SelectChangeEvent<Filter[]>) => {
    setSelectedValues(event.target.value as Filter[]);
    setPage(0);
  };

  const handleDelete = (valueToDelete: Filter) => {
    const newSelectedValues = selectedValues.filter((value) => value !== valueToDelete);
    setSelectedValues(newSelectedValues);
    setPage(0);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        borderRadius: '8px',
        border: '1px solid #0D1B273B',
        minHeight: 40,
        maxHeight: 40,
        overflow: 'hidden',
      }}
    >
      <FormControl variant="outlined" fullWidth size="small" sx={{ position: 'relative' }}>
        <InputLabel
          sx={{
            position: 'absolute',
            top: isEmptyArray(selectedValues) ? 10 : 14,
            left: 14,
            color: 'black',
            fontSize: '14px',
            '&.Mui-focused': {
              color: 'inherit',
            },
            transform: 'none',
            padding: '0 4px',
            zIndex: 1,
          }}
        >
          {label}
        </InputLabel>
        <Select
          label={label}
          multiple
          value={selectedValues ?? []}
          onChange={handleChange}
          renderValue={(selected) => (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                overflow: 'hidden',
              }}
            >
              {selected.slice(0, 2).map((value) => (
                <Chip
                  key={value}
                  label={getLabel(value)}
                  onDelete={() => handleDelete(value)}
                  onMouseDown={(event) => event.stopPropagation()}
                  sx={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    fontSize: '13px',
                    '.MuiChip-deleteIcon': { color: 'white' },
                  }}
                />
              ))}
              {selectedValues.length > 2 && (
                <Chip
                  label={`+${selectedValues.length - 2}`}
                  sx={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
          )}
          autoWidth={true}
          MenuProps={{
            style: { minWidth: '50px !important', maxWidth: '50px !important' },
          }}
          sx={{
            minWidth: 180,
            maxHeight: 50,
            flexGrow: 1,
            '.MuiOutlinedInput-notchedOutline': {
              border: 'none !important',
            },
            '& .MuiSelect-select': {
              display: 'flex',
              paddingRight: 6,
              paddingLeft: 12,
              alignItems: 'center',
              ...selectStyle,
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option}>
              <Checkbox checked={(selectedValues ?? []).indexOf(option) != -1} />
              <ListItemText primary={getLabel(option)} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};
